// WHAT: Status display component for configured OAuth Client Credentials
// WHY:  Separates status UI from main component to reduce complexity
// DOMAIN: Settings → Yazio
// DEPENDENCIES: React, @/components/ui, lucide-react

import { CheckCircle, Edit3, Loader2, X } from "lucide-react";
import React from "react";

import { Button, Card, Heading, Stack } from "@/components/ui";

interface OAuthClientCredentialsStatusProperties {
  /** Handler for edit action */
  onEdit: () => void;
  /** Handler for remove action */
  onRemove: () => void;
  /** Whether form is in saving state */
  saving: boolean;
}

/**
 * WHAT:
 *   Status display component for configured OAuth Client Credentials.
 *
 * WHY:
 *   Separates status UI from main component to reduce complexity.
 *
 * @param root0
 *
 * @param root0.saving
 *
 * @param root0.onEdit
 *
 * @param root0.onRemove
 *
 * @returns {JSX.Element} OAuth Client Credentials status component
 *
 */
export const OAuthClientCredentialsStatus: React.FC<OAuthClientCredentialsStatusProperties> = ({
  onEdit,
  onRemove,
  saving,
}) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <Stack direction="col" gap={4}>
        {/* Section Header */}
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              OAuth Client Credentials Configured
            </Heading>
          </Stack>

          <Stack direction="row" gap={2}>
            <Button onClick={onEdit} size="md" variant="ghost">
              <Edit3 className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              disabled={saving}
              onClick={() => {
                onRemove();
              }}
              size="md"
              variant="danger"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Remove
            </Button>
          </Stack>
        </Stack>

        {/* Management Content */}
        <Stack direction="col" gap={2}>
          <p className="text-slate-300 text-sm">
            OAuth Client Credentials are configured and stored securely in the keyring. These are
            used for API authentication and are never displayed in the frontend.
          </p>
          <p className="text-slate-500 text-xs">
            The Client Secret is highly sensitive and is only used by the backend for secure API
            calls.
          </p>
        </Stack>
      </Stack>
    </Card>
  );
};

